import { useContext } from 'react';
import { useImages } from '../context/ImageContext';
import { GameStateContext } from '../context/GameStateContext';
const { ShownName } = require('@tta/engine/Utilities/ShownNames');

// Image for each kind of amount
const Icons = {
    resource: 'Resource',
    resources: 'Resource',
    food: 'Food',
    science: 'Science',
    culture: 'Culture',
    CA: 'CivilAction',
    MA: 'MilitaryAction',
    strength: 'Strength',
    'happy faces': 'Happiness',
    population: 'YellowCube',
    colonization: 'ExplorationResource',
    'blue tokens': 'BlueCube',
    'yellow tokens': 'YellowCube',
}

// An amount followed by its icon, e.g. "2 [resource icon]"; signed adds a + to positive amounts; title: hover text in
// place of the amount's own (e.g. where a total comes from); numberClass: a class for the number alone (e.g. a lowered cost)
// What an action would set off (cards reacting to it), after its cost: "(+1 [science] +1 [culture] Maria Theresa)"
export const TriggerGains = ({Gains}) => (Gains ?? []).map(gain => {
    const [source, text] = gain.split(/: (.*)/s);
    return <span key={gain} className={`tta-gain-note${/^-/.test(text) ? ' loss' : ''}`}> (<IconText text={text.replace(/,/g, '')}/> {source})</span>;
});

function Amount({value, kind, signed = false, title, numberClass})
{
    const { images } = useImages();
    const image = images[Icons[kind]];
    const text = `${signed && value > 0 ? '+' : ''}${value}`;
    if (!image)
    {
        return <span>{text} {kind}</span>;
    }
    // Actions are counted out, one icon each: 2 military actions are [MA][MA]
    if ((kind === 'CA' || kind === 'MA') && Number.isInteger(value) && value > 0 && value <= 8)
    {
        return (
            <span className="tta-amount" title={`${text} ${kind}`}>
                {signed && '+'}{Array.from({ length: value }, (_, index) => <img key={index} src={image.src} alt={kind}/>)}
            </span>
        );
    }
    return (
        <span className="tta-amount" title={title ?? `${text} ${kind}`}>
            {numberClass ? <span className={numberClass}>{text}</span> : text}<img src={image.src} alt={kind}/>
        </span>
    );
}

// The icon of a kind of amount alone (e.g. an action, strength)
export function ActionIcon({kind})
{
    const { images } = useImages();
    const image = images[Icons[kind]];
    return image ? <span className="tta-amount"><img src={image.src} alt={kind}/></span> : <span>{kind}</span>;
}

// A cost from the engine ("2 resources", "+1 culture", "2 MA") with its icon; anything else as plain text
export function CostLabel({cost})
{
    const parts = (cost ?? '').split(', ');
    if (parts.length > 1)
    {
        return <>{parts.map((part, index) => <span key={index}>{index > 0 && ' '}<CostLabel cost={part}/></span>)}</>;
    }
    const match = /^([+-]?\d+) (resources?|food|science|culture|CA|MA)$/.exec(cost ?? '');
    if (match)
    {
        return <Amount value={Number(match[1])} kind={match[2]} signed={match[1].startsWith('+')}/>;
    }
    // e.g. "replace with CA": the words, then the action's icon
    const action = /^(.*\D) (CA|MA)$/.exec(cost ?? '');
    return action ? <span>{action[1]} <ActionIcon kind={action[2]}/></span> : <span>{cost}</span>;
}

// Words that can follow a number in text, and the icon kind they mean
const TextKinds = {
    resource: 'resources', resources: 'resources',
    food: 'food',
    science: 'science',
    culture: 'culture',
    strength: 'strength',
    'happy face': 'happy faces', 'happy faces': 'happy faces',
    'civil action': 'CA', 'civil actions': 'CA', CA: 'CA',
    'military action': 'MA', 'military actions': 'MA', MA: 'MA',
    colonization: 'colonization',
    'blue token': 'blue tokens', 'blue tokens': 'blue tokens',
    'yellow token': 'yellow tokens', 'yellow tokens': 'yellow tokens',
};
// Longest words first so "civil actions" wins over "civil action"
const TextPattern = new RegExp(
    `([+-]?\\d+) (${Object.keys(TextKinds).sort((a, b) => b.length - a.length).map(word => word.replace(/ /g, '\\s')).join('|')})\\b`, 'g');

// Text with every "<number> <resource word>" shown as the amount and its icon,
// e.g. "Each civilization gains 2 resources." -> "Each civilization gains 2 [resource icon]."
// Cards named in it (the engine's CardRef / HiddenRef) are drawn as the cards; in a game, its players' names are in their colors.
export function IconText({text})
{
    if (typeof text !== 'string')
    {
        return text ?? null;
    }
    if (text.includes('⟦'))
    {
        // (required here: CardToken's cards use this file)
        const CardToken = require('./CardToken').default;
        // (a card face down to all but its owner, as the server shows it to anyone else: e.g. replayed in a review)
        text = require('@tta/engine/Utilities/CardRefs').ForViewer(text, null);
        return <>{text.split(/(⟦[cb]:[^⟧]*⟧)/).map((piece, index) => {
            const token = piece.match(/^⟦([cb]):([^⟧]*)⟧$/);
            return token ? <CardToken key={index} kind={token[1]} body={token[2]}/> : <WordIcons key={index} text={piece}/>;
        })}</>;
    }
    return <WordIcons text={text}/>;
}

// The game's players named in text, in their colors (bold), the rest as WordAmounts
function WordIcons({text})
{
    // (cards kept without a space in their names, as printed)
    text = ShownName(text);
    const game = useContext(GameStateContext);
    const names = (game?.gameState?.playerStates ?? []).map(player => player.name).filter(name => name);
    if (names.length === 0)
    {
        return <WordAmounts text={text}/>;
    }
    // (whole names only: longest first, so one that contains another wins)
    const escaped = [...names].sort((a, b) => b.length - a.length).map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(?<![\\p{L}\\d])(${escaped.join('|')})(?![\\p{L}\\d])`, 'u');
    return <>{text.split(pattern).map((piece, index) => index % 2 === 1
        ? <b key={index} style={{ color: game.colors?.[names.indexOf(piece)] }}>{piece}</b>
        : <WordAmounts key={index} text={piece}/>)}</>;
}

// Text with each "<number> <resource word>" as the amount and its icon, and "vs" (strengths compared: "5 [strength]
// vs 3 [strength]") in bold
function WordAmounts({text})
{
    const parts = [];
    let last = 0;
    const plain = (piece, key) => piece.split(/\b(vs)\b/).map((bit, index) => index % 2 === 1 ? <b key={`${key}vs${index}`}>vs</b> : bit);
    for (const match of text.matchAll(TextPattern))
    {
        parts.push(...plain(text.slice(last, match.index), last));
        parts.push(<Amount key={match.index} value={Number(match[1])} kind={TextKinds[match[2]]} signed={match[1].startsWith('+')}/>);
        last = match.index + match[0].length;
    }
    parts.push(...plain(text.slice(last), last));
    return <>{parts}</>;
}

export default Amount;